import React, { useState } from "react";
import { Button } from "./ui/Button";
import { Modal } from "./ui/Modal";
import { Input } from "./ui/Input";

export interface CameraFeedProps {
  label: string;
  status: "idle" | "pass" | "fail" | "read" | "no read";
  barcode: string;
  lastScanTime: string | null;
  cameraState: "initializing" | "ready" | "error" | "unavailable";
  videoRef: React.RefObject<HTMLVideoElement | null>;
  onVideoError: () => void;
  isDetecting?: boolean;
  onManualInput?: (code: string) => void; // NEW: Callback for manual input
}

export const CameraFeed: React.FC<CameraFeedProps> = ({
  label,
  status,
  barcode,
  lastScanTime,
  cameraState,
  videoRef,
  onVideoError,
  isDetecting = false,
  onManualInput,
}) => {
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualCode, setManualCode] = useState("");

  // Handle manual code submission
  const handleManualSubmit = () => {
    if (manualCode.trim() && onManualInput) {
      onManualInput(manualCode.trim());
      setManualCode("");
      setShowManualInput(false);
    }
  };

  // Determine status class and label
  const getStatusClass = () => {
    if (status === "pass") return "bg-green-500 text-white";
    if (status === "fail") return "bg-red-500 text-white";
    if (status === "read") return "bg-orange-500 text-white";
    if (status === "no read") return "bg-gray-400 text-white";
    return "bg-gray-200 text-gray-700";
  };

  const getStatusLabel = () => {
    if (status === "pass") return "PASS";
    if (status === "fail") return "FAIL";
    if (status === "read") return "READ";
    if (status === "no read") return "NO READ";
    return "READY";
  };

  const renderCameraStateOverlay = () => {
    if (cameraState === "initializing") {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 text-white">
          <div className="text-center">
            <svg
              className="animate-spin h-8 w-8 mx-auto mb-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Initializing camera...
          </div>
        </div>
      );
    }

    if (cameraState === "error") {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 text-white">
          <div className="text-center p-4">
            <svg
              className="h-12 w-12 mx-auto mb-2 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="text-red-300 font-medium mb-1">Camera Error</div>
            <div className="text-xs text-gray-300">
              Permission denied or device in use
            </div>
          </div>
        </div>
      );
    }

    if (cameraState === "unavailable") {
      return (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-90 text-white">
          <div className="text-center p-4">
            <svg
              className="h-12 w-12 mx-auto mb-2 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <div className="text-gray-300 font-medium mb-1">
              Camera Not Available
            </div>
            <div className="text-xs text-gray-400">
              Connect additional camera to use this slot
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const statusClass = getStatusClass();
  const statusLabel = getStatusLabel();

  return (
    <div>
      <div className="flex flex-row gap-6">
        {/* Camera feed and status */}
        <div className="mb-2 w-2/3">
          <div
            className={`w-full h-[120px] flex items-center justify-center rounded-lg ${statusClass} text-2xl font-bold relative`}
          >
            {statusLabel}
            {isDetecting && cameraState === "ready" && (
              <div className="absolute right-3 top-3 flex items-center">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
                <span className="ml-2 text-xs font-normal">Scanning</span>
              </div>
            )}
          </div>
        </div>

        <div className="relative bg-black rounded-lg overflow-hidden h-32 w-1/3">
          <video
            ref={videoRef}
            className="w-full h-full object-cover"
            autoPlay
            playsInline
            muted
            onError={onVideoError}
          />

          {renderCameraStateOverlay()}

          {cameraState === "ready" && barcode && (
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm">
              <div className="flex items-center">
                <svg
                  className="w-4 h-4 mr-1"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                  />
                </svg>
                <span className="truncate">{barcode}</span>
              </div>
            </div>
          )}
        </div>

        {/* Barcode info */}
        <div className="w-1/3">
          <div className="p-3 bg-gray-100 rounded-lg h-[120px] relative">
            <div className="mb-2 flex flex-row justify-between items-center">
              <span className="text-sm text-gray-600 font-medium">{label}</span>
              {cameraState === "ready" && !barcode && onManualInput && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowManualInput(true)}
                  className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 !text-gray-400 border-none"
                >
                  Manual
                </Button>
              )}
            </div>
            <div className="mb-2">
              <span className="text-sm text-gray-600">Bar Code #:</span>
              <div className="p-2 bg-white rounded border border-gray-300 font-mono text-sm">
                {barcode || "---"}
              </div>
            </div>

            {lastScanTime && (
              <div className="text-xs text-gray-500 mt-1">
                Last scan: {lastScanTime}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Manual Input Modal */}
      <Modal
        isOpen={showManualInput}
        onClose={() => setShowManualInput(false)}
        title={`Enter Barcode Manually - ${label}`}
        size="small"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowManualInput(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleManualSubmit}>
              Submit
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            Enter the barcode manually if it cannot be scanned due to fading or
            damage.
          </p>
          <Input
            value={manualCode}
            onChange={(e) => setManualCode(e.target.value)}
            placeholder="Enter barcode"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleManualSubmit();
              }
            }}
          />
        </div>
      </Modal>
    </div>
  );
};
